import {Component, OnInit, OnDestroy, ViewChild, SimpleChanges} from '@angular/core';
import {Subscription, Observable} from "rxjs";

import {CloudApiService} from "../../services/cloud-api.service";
import {UserSessionService} from "../../services/user-session.service";
import {AuthenticationService} from "../../services/authentication.service";
import {SelectLibraryModal} from "../modals/select-library/select-library.modal.component";

import {BranchDto} from "../../services/dtos/branch.dto";
import {DashboardDto} from "../../services/dtos/dashboard.dto";
import {DashboardCharts} from "./dashboard-charts";
import {SearchDto} from "../../services/dtos/search.dto";

import moment = require("moment");
import {DashboardTerminalDto} from "../../services/dtos/dashboard-terminal.dto";
import {NgbPopoverConfig} from '@ng-bootstrap/ng-bootstrap';
import { BaseChartDirective } from "ng2-charts";
import {AlarmComponent} from "../alarms/alarm.component";
import {OfflineImportFileType} from "../../services/dtos/enums/offline-import-file-type.enum";
declare let Chart:any;

enum State {
    None,
    Loading,
    Ready
}

@Component({
    selector: 'dashboard',
    template: require('./dashboard.component.html'),
    styles: [ require('./dashboard.component.css') ],
    providers: [NgbPopoverConfig]
})
export class DashboardComponent implements OnInit, OnDestroy {
    states = State;
    state: State = State.None;
    errorMessage: string;
    showCharts: boolean;
    chartDataChanged: boolean;
    showCertainTerminals: boolean = false;

    branches: BranchDto[] = [];
    certainTerminals: DashboardTerminalDto[] = [];
    commonTerminals: DashboardTerminalDto[] = [];
    selectedBranch: BranchDto = new BranchDto();
    dashboardData: DashboardDto = new DashboardDto();
    dashboardCharts: DashboardCharts = new DashboardCharts();
    @ViewChild(BaseChartDirective)
    public chart: BaseChartDirective;
    @ViewChild(AlarmComponent)
    private alarmComponent: AlarmComponent;
    private librarySubscription: Subscription;
    private pollingTimerId: number;
    private connectivityPollerId: number;
    protected readonly OfflineImportFileType = OfflineImportFileType;

    constructor(
        private userSession: UserSessionService,
        private authenticationService: AuthenticationService,
        private cloudApiService: CloudApiService,
        private selectLibraryModal: SelectLibraryModal,
        config: NgbPopoverConfig
    ) {
        config.placement = 'left';
        config.triggers = 'hover'

    }

    public ngOnInit(): void {
        Chart.defaults.global.defaultFontColor = "#fff";

        this.populateChartColours();

        this.librarySubscription = this.userSession.library.subscribe(library => {
            this.state = State.Loading;

            if (library)
                this.loadBranches();
            else if (!this.userSession.libraryId && this.authenticationService.authenticated)
                this.selectLibraryModal.open();
        });
    }

    public ngOnDestroy(): void {
        this.librarySubscription.unsubscribe();
        window.clearTimeout(this.pollingTimerId);
    }

    public changeBranch(branch: BranchDto): void {
        this.selectedBranch = branch;
        this.userSession.setBranchId(branch.id);

        this.updateAll();
    }

    public updateAll(): void {
        this.updateData();
        this.updateConnectivity();
    }

    public updateData(showLoading: boolean = true): void {
        this.alarmComponent.updateData(this.selectedBranch.id, showLoading);
        this.errorMessage = null;
        if (showLoading)
            this.state = State.Loading;

        this.retrieveStatistics().subscribe(() => {
            this.retrieveReportData().subscribe(() =>{
                this.showCharts =
                    (this.dashboardCharts.issuesReturnsChartData[0] > 0 || this.dashboardCharts.issuesReturnsChartData[1] > 0) ||
                    this.dashboardCharts.gateCountsChartData[0].data.length > 0;

                this.state = State.Ready;
            });
        });

        // Refresh charts every 30 seconds
        window.clearTimeout(this.pollingTimerId);
        this.pollingTimerId = window.setTimeout(() => {
            this.updateData(false);
        }, 30 * 1000);
    }

    public getBranch(branchId: number) : string {
        for (let i = 0; i < this.branches.length; i++) {
            if (this.branches[i].id == branchId)
                return this.branches[i].name
        }
    }

    public getBranchByConfigBranchId(configId: number, configBranchId: number) : string {
        if (configBranchId == 0)
            return "All Branches";

        for (let i = 0; i < this.branches.length; i++) {
            if (this.branches[i].configId == configId && this.branches[i].configBranchId == configBranchId)
                return this.branches[i].name
        }
    }

    public terminalTimespan(dateTime: number) : string {
        let minutes = moment.utc(dateTime).local().fromNow();
        return minutes.toString();
    }

    public isHealthy(dateTime: number) : boolean {
        let timeDifference = moment().diff(moment.utc(dateTime).local(), 'minutes');
        return timeDifference < 30;
    }

    protected handleError(error: any, invalidateState: boolean = true): Observable<any> {
        if (invalidateState)
            this.state = State.None;

        this.errorMessage = error;
        return Observable.throw(error);
    }

    private updateConnectivity(showLoading: boolean = true) : void {
        this.errorMessage = null;
        if (showLoading)
            this.state = State.Loading;

        this.cloudApiService.getDashboardTerminals(this.selectedBranch.id).subscribe(
            dashboardTerminals => {
                this.commonTerminals = [];
                this.certainTerminals = [];
                for (let i = 0; i < dashboardTerminals.length; i++){
                    let terminal = dashboardTerminals[i];
                    let minutesDifference = moment().diff(moment.utc(terminal.latestHeartbeatDateUtc).local(), 'minutes');
                    let monthsDifference = moment().diff(moment.utc(terminal.latestHeartbeatDateUtc).local(), 'months');
                    if (minutesDifference<10 || monthsDifference>=1){
                        this.certainTerminals.push(terminal);
                    }else{
                        this.commonTerminals.push(terminal)
                    }
                }
                this.state = State.Ready;
            },
            error => {
                this.errorMessage = error;
                this.state = State.None;
            }
        );

        // Refresh terminal connectivity every 30 minutes
        window.clearTimeout(this.connectivityPollerId);
        this.connectivityPollerId = window.setTimeout(() => {
            this.updateConnectivity(false);
        }, 20 * 60 * 1000);
    }

    private retrieveStatistics(): Observable<void> {
        let searchQuery = this.createSearchQuery();
        searchQuery.offlineImportFileConfigId = this.selectedBranch.configId;
        searchQuery.offlineImportFileConfigBranchId = this.selectedBranch.configBranchId;
        return this.cloudApiService.getDashboard(this.selectedBranch.id, searchQuery)
            .map(dashboardResult => {
                let dashboard = new DashboardDto().fromObject(dashboardResult);

                this.chartDataChanged = this.dashboardData.chartDataChanged(dashboard);
                this.dashboardData = dashboard;

                // Only refresh graphs if data has changed
                if (this.chartDataChanged) {
                    let issuesReturnsData: number[] = [];
                    issuesReturnsData.push(dashboard.totalReturns);
                    issuesReturnsData.push(dashboard.totalRenews);
                    issuesReturnsData.push(dashboard.totalIssues);
                    this.dashboardCharts.issuesReturnsChartData = issuesReturnsData;
                }
            })
            .catch(error => this.handleError(error));
    }

    private retrieveReportData(): Observable<boolean> {
        // Only refresh graphs if data has changed
        if (!this.chartDataChanged)
            return Observable.of(false);

        let searchQuery = this.createSearchQuery();
        return this.cloudApiService.getDashboardReport(this.selectedBranch.id, searchQuery)
            .map(reportData => {
                let gateLabels: string[] = [];
                let gateData: any[] = [{data: [], label: 'In'}, {data: [], label: 'Out'}];

                for (let data of reportData) {
                    gateLabels.push(moment.utc(data.dateTimeUtc).local().format('h:mm a'));
                    gateData[0].data.push(data.totalInCount);
                    gateData[1].data.push(data.totalOutCount);
                }
                this.dashboardCharts.gateCountsChartLabels = gateLabels;
                this.dashboardCharts.gateCountsChartData = gateData;
                // Force to refresh the graph
                setTimeout(() => {
                    if (this.chart)
                        this.chart.ngOnChanges({} as SimpleChanges);
                }, 1);

                return true;
            })
            .catch(error => this.handleError(error));
    }

    private populateChartColours() : void {
        for (let i = 0; i < 24; i++) {
            this.dashboardCharts.gateCountsChartColors[0].backgroundColor.push("#c1df9f");
            this.dashboardCharts.gateCountsChartColors[1].backgroundColor.push("#a9d279");
        }
    }

    private loadBranches(): void {
        this.cloudApiService.getBranches().subscribe(
            branches => {
                let allBranch = new BranchDto();
                allBranch.id = 0;
                allBranch.name = "All Branches";

                this.branches = branches;
                this.branches.unshift(allBranch);

                let previousBranch = branches.find(x => x.id === this.userSession.branchId);
                if (previousBranch !== undefined)
                    this.changeBranch(previousBranch);
                else
                    this.changeBranch(allBranch);
            },
            error => {
                this.errorMessage = error;
                this.state = State.None;
            }
        );
    }

    private createSearchQuery(): SearchDto {
        let searchQuery = new SearchDto();

        searchQuery.fromDateUtc = moment().startOf('day').toDate();
        searchQuery.toDateUtc = moment(searchQuery.fromDateUtc).add({'hours': 24}).toDate();

        return searchQuery;
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/components/dashboard/dashboard.component.ts